import React, { useEffect, useRef } from "react";
import AppIcon from "./AppIcon";

/**
 * A professional, reusable delete confirmation modal.
 * @param {boolean} isOpen - Controls visibility
 * @param {Function} onClose - Called when canceling or closing
 * @param {Function} onConfirm - Called when confirming deletion
 * @param {boolean} isDeleting - Shows loading state on delete button
 */
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, isDeleting = false }) => {
  const modalRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
      if (e.key === "Enter" && isOpen && !isDeleting) onConfirm();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, onConfirm, isDeleting]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="
          w-full max-w-[400px] 
          bg-app-surface border border-white/10 
          rounded-2xl shadow-2xl 
          overflow-hidden 
          animate-in zoom-in-95 duration-200
        "
      >
        {/* Header */}
        <div className="p-6 pb-2">
          <div className="flex items-center gap-3 text-red-500 mb-2">
            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
              <AppIcon name="TrashIcon" size={20} />
            </div>
            <h2 id="modal-title" className="text-xl font-bold tracking-tight text-text-primary">
              Delete Message?
            </h2>
          </div>
          <p className="text-sm leading-relaxed text-text-secondary">
            Are you sure you want to delete this message? This action cannot be undone and will remove the message for everyone.
          </p>
        </div>

        {/* Footer Actions */}
        <div className="p-6 pt-6 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={onClose}
            className="
              flex-1 px-4 py-2.5 
              rounded-xl text-sm font-bold
              text-text-secondary hover:text-text-primary
              bg-white/5 hover:bg-white/10
              transition-all border border-white/5
            "
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isDeleting}
            onClick={onConfirm}
            className="
              flex-1 px-4 py-2.5 
              rounded-xl text-sm font-bold
              text-white bg-red-500 hover:bg-red-600
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all shadow-lg shadow-red-500/20
              flex items-center justify-center gap-2
            "
          >
            {isDeleting ? (
              <>
                <AppIcon name="Spinner" size={16} className="animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Message"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
